import Layout from "../../components/Layout.tsx";
import { Checkbox } from "../../components/Checkbox.tsx";
import { Text } from "../../components/Text.tsx";
import { StepProps } from "./AddNewConnection.tsx";
import { useEffect, useRef, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/Table.tsx";
import { Select } from "../../components/Select.tsx";
import { SyncModule } from "../../types.ts";
import { useNewConnectionContext } from "../../context/NewConnectionContext.tsx";
import { useApplicationInfoContext } from "../../context/ApplicationInfoContext.tsx";
import { fetchExactModules } from "../../services/ApiService.ts";
import Spinner from "../../components/Spinner.tsx";

interface JournalDraftMapping {
  source: string;
  destination: string;
  type: "journal";
}

const MapModulesStep = ({
  handleButtonBackClick,
  handleButtonNextClick,
  stepIndicator,
}: StepProps) => {
  const { applicationInfo, dispatch: appInfoDispatch } = useApplicationInfoContext();

  const exactOnlineModules = applicationInfo.availableModules;
  const easyflexJournals = applicationInfo.availableJournals;
  const { newConnectionData, dispatch: connectionDispatch } = useNewConnectionContext();
  const [modulesLoading, setModulesLoading] = useState(false);
  const [modulesError, setModulesError] = useState<string | null>(null);

  // Check if we have existing journal mappings in context
  const existingJournalMappings = newConnectionData.mappings?.filter(mapping => mapping.type === "journal") || [];

  const [moduleMappings, setModuleMappings] = useState<JournalDraftMapping[]>(existingJournalMappings);

  // Pre-fill journal mappings from Easyflex financial admin when Exact modules are loaded
  const hasPreFilled = useRef(false);
  useEffect(() => {
    if (hasPreFilled.current) return;
    const financialAdmin = applicationInfo.easyflexFinancialAdmin;
    if (!financialAdmin || exactOnlineModules.length === 0) return;
    if (existingJournalMappings.length > 0) return;

    const journalCodeMap: Record<string, string | null | undefined> = {
      verkoop: financialAdmin.codeVerkoopboek,
      inkoop: financialAdmin.codeInkoopboek,
      loon: financialAdmin.codeMemoriaal,
    };

    const preFilled: JournalDraftMapping[] = Object.entries(journalCodeMap)
      .filter(([journalId, code]) => code && modulesForJournal(journalId).some((module) => module.Code === code))
      .map(([journalId, code]) => ({ source: journalId, destination: code!, type: "journal" }));

    if (preFilled.length > 0) {
      setModuleMappings(preFilled);
      hasPreFilled.current = true;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- listing this would re-run the effect on changes it should ignore
  }, [applicationInfo.easyflexFinancialAdmin, exactOnlineModules]);

  useEffect(() => {
    const division = Number(newConnectionData.destinationAdministrationId);
    const systemAuthorizationId = Number(newConnectionData.systemAuthorizationId);
    if (!division || !systemAuthorizationId) {
      setModulesError(null);
      appInfoDispatch({ type: "UPDATE_MODULES", modules: [] });
      return;
    }

    let cancelled = false;

    const loadModules = async () => {
      setModulesLoading(true);
      setModulesError(null);
      try {
        const { data, error } = await fetchExactModules(division, systemAuthorizationId);
        if (cancelled) {
          return;
        }
        if (error) {
          throw new Error(error);
        }
        const modules = data ?? [];
        appInfoDispatch({ type: "UPDATE_MODULES", modules });

        const allowedCodes = new Set(modules.map((module) => module.Code));
        setModuleMappings((prev) =>
          prev.map((mapping) => (
            mapping.destination && !allowedCodes.has(mapping.destination)
              ? { ...mapping, destination: "" }
              : mapping
          ))
        );
      } catch (error) {
        if (cancelled) {
          return;
        }
        console.error("Failed to fetch Exact Online modules:", error);
        setModulesError(error instanceof Error ? error.message : "Kan Exact Online dagboeken niet ophalen");
        appInfoDispatch({ type: "UPDATE_MODULES", modules: [] });
      } finally {
        if (!cancelled) {
          setModulesLoading(false);
        }
      }
    };

    void loadModules();

    return () => {
      cancelled = true;
    };
  }, [newConnectionData.destinationAdministrationId, newConnectionData.systemAuthorizationId, appInfoDispatch]);

  // Helper functions for managing state
  const handleCheckboxChange = (journalId: string, isChecked: boolean) => {
    setModuleMappings((prev) =>
      isChecked
        ? [...prev, { source: journalId, destination: "", type: "journal" }]
        : prev.filter((mapping) => mapping.source !== journalId)
    );
  };

  const handleSelectChange = (journalId: string, exactId: string) => {
    setModuleMappings((prev) =>
      prev.map((mapping) =>
        mapping.source === journalId
          ? { ...mapping, destination: exactId }
          : mapping
      )
    );
  };

  const isSelected = (journalId: string) =>
    moduleMappings.some((mapping) => mapping.source === journalId);

  const getSelectedOption = (journalId: string) =>
    moduleMappings.find((mapping) => mapping.source === journalId)
      ?.destination || "";

  const modulesForJournal = (journalId: string) => {
    const journalTypes: Record<string, number> = { verkoop: 20, inkoop: 22, loon: 90 };
    return exactOnlineModules.filter(
      (module) => module.Type === journalTypes[journalId] || module.Code === getSelectedOption(journalId)
    );
  };

  const handleNextClick = () => {
    if (moduleMappings.length === 0) {
      alert("Selecteer minimaal één journaal om te synchroniseren!");
      return;
    }
    const missingDestination = moduleMappings.some((mapping) => !mapping.destination);
    if (missingDestination) {
      alert("Selecteer een Exact Online dagboek voor elk geselecteerd Easyflex dagboek!");
      return;
    }
    // Preserve VAT code and betalingskorting mappings for modules that are still selected, drop the rest
    const selectedModules = new Set(moduleMappings.map((mapping) => mapping.source.toUpperCase()));
    const existingVATMappings = (newConnectionData.mappings ?? []).filter(
      (mapping) =>
        (mapping.type === "vatCode" || mapping.type === "betalingskorting") && selectedModules.has(mapping.module)
    );
    const journalMappingIds = moduleMappings.map((mapping) => ({
      source: mapping.source,
      destination: mapping.destination,
      type: "journal",
      module: mapping.source.toUpperCase() as SyncModule,
    }));
    const allMappings = [...existingVATMappings, ...journalMappingIds];

    connectionDispatch({ type: "UPDATE_FIELD", key: 'mappings', value: allMappings });
    handleButtonNextClick?.(allMappings);
  };

  const isUpdating = newConnectionData.id && newConnectionData.id > 0;

  // Optional sync features
  const handleSyncDebiteurenChange = (isChecked: boolean) => {
    connectionDispatch({ type: "UPDATE_FIELD", key: "enableSyncDebiteuren", value: isChecked });
  };

  const handleSyncGrootboekenChange = (isChecked: boolean) => {
    connectionDispatch({ type: "UPDATE_FIELD", key: "enableSyncGrootboeken", value: isChecked });
  };

  const handleSyncKostenplaatsenChange = (isChecked: boolean) => {
    connectionDispatch({ type: "UPDATE_FIELD", key: "enableSyncKostenplaatsen", value: isChecked });
  };

  return (
    <Layout
      title={isUpdating ? "Koppeling aanpassen" : "Nieuwe koppeling maken"}
      stepTitle="Dagboeken koppelen"
      onButtonBackClick={handleButtonBackClick}
      onButtonNextClick={handleNextClick}
      stepIndicator={stepIndicator}
    >
      <Text className="mb-12">
        Selecteer een dagboek om te synchroniseren en koppel dit aan een Exact Online-dagboek.
      </Text>
      {modulesLoading ? (
        <div className="flex min-h-[160px] items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader className="w-0"></TableHeader>
              <TableHeader>Easyflex dagboek</TableHeader>
              <TableHeader>Exact Online dagboek</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {easyflexJournals.map((journal) => (
              <TableRow key={journal.id}>
                <TableCell>
                  <Checkbox
                    color="primary"
                    checked={isSelected(journal.id)}
                    onChange={(isChecked) =>
                      handleCheckboxChange(journal.id, isChecked)
                    }
                  />
                </TableCell>
                <TableCell
                  className="cursor-pointer select-none"
                  onClick={() => handleCheckboxChange(journal.id, !isSelected(journal.id))}
                >
                  {journal.name}
                </TableCell>
                <TableCell>
                  <Select
                    name="exactField"
                    disabled={!isSelected(journal.id)}
                    value={getSelectedOption(journal.id)}
                    onChange={(e) =>
                      handleSelectChange(journal.id, e.target.value)
                    }
                  >
                    <option value="" disabled>
                      {modulesError ?? (exactOnlineModules.length ? "Selecteer één dagboek" : "Geen dagboeken gevonden")}
                    </option>
                    {modulesForJournal(String(journal.id)).map((module) => (
                      <option key={module.ID} value={module.Code}>
                        {module.Description}
                      </option>
                    ))}
                  </Select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <div className="mt-8">
        <Text className="mb-4 font-medium">Optionele synchronisatie functies</Text>
        <div className="space-y-4">
          <div>
            <div className="flex items-center gap-3 cursor-pointer select-none" onClick={() => handleSyncDebiteurenChange(!(newConnectionData.enableSyncDebiteuren ?? false))}>
              <span onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  color="primary"
                  checked={newConnectionData.enableSyncDebiteuren ?? false}
                  onChange={handleSyncDebiteurenChange}
                />
              </span>
              <span>Synchroniseer debiteuren</span>
            </div>
            <p className="text-sm text-gray-500 ml-7">Synchroniseert debiteuren van Easyflex naar Exact Online voordat journaalposten worden verwerkt.</p>
          </div>
          <div>
            <div className="flex items-center gap-3 cursor-pointer select-none" onClick={() => handleSyncGrootboekenChange(!(newConnectionData.enableSyncGrootboeken ?? false))}>
              <span onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  color="primary"
                  checked={newConnectionData.enableSyncGrootboeken ?? false}
                  onChange={handleSyncGrootboekenChange}
                />
              </span>
              <span>Synchroniseer grootboeken</span>
            </div>
            <p className="text-sm text-gray-500 ml-7">Synchroniseert grootboekrekeningen van Easyflex naar Exact Online voordat journaalposten worden verwerkt.</p>
          </div>
          <div>
            <div className="flex items-center gap-3 cursor-pointer select-none" onClick={() => handleSyncKostenplaatsenChange(!(newConnectionData.enableSyncKostenplaatsen ?? false))}>
              <span onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  color="primary"
                  checked={newConnectionData.enableSyncKostenplaatsen ?? false}
                  onChange={handleSyncKostenplaatsenChange}
                />
              </span>
              <span>Synchroniseer kostenplaatsen</span>
            </div>
            <p className="text-sm text-gray-500 ml-7">Synchroniseert kostenplaatsen van Easyflex naar Exact Online voordat journaalposten worden verwerkt.</p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default MapModulesStep;
